import { Grid, Link } from "@mui/material";
import { Stack } from "@mui/system";
import * as React from "react";
import { useParams } from "react-router-dom";
import { AuthenticationContext, ImagesContext } from "../../../../Context";
import { getHeaders } from "../../../../utils/HeaderHelper";
import CustomChip from "../../../../shared/CustomChip";
import AssignmentHistoryModal from "./AssignmentHistoryModal";
import PayerProgramCard from "./PayerProgramCard";
import useFetchAssignmentHistories from "../../../../hooks/patients/useFetchAssignmentHistories";

interface PayerProgramGroup {
  patient_id: number;
  payer_id: number;
  payer_name: string;
  plan_year: string;
  programs: {
    payer_program_id: number;
    program_name: string;
    eligibility_status: string | null;
    eligibility_determined_at: string | null;
    qualifying_medication_names: string[];
    enrollment_status: string | null;
    enrolled_at: string | null;
    unenrolled_at: string | null;
  }[];
}

const PayerPrograms: React.FC = () => {
  const { id: patientId } = useParams();
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const [payerData, setPayerData] = React.useState<PayerProgramGroup | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const [reFetchingKey, setReFetchingKey] = React.useState(Math.random());

  const { assignmentHistories, loading: historiesLoading } = useFetchAssignmentHistories(
    patientId,
    "payer_program",
    reFetchingKey
  );

  React.useEffect(() => {
    if (!patientId) return;
    fetchPayerPrograms();
  }, [patientId]);

  const fetchPayerPrograms = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/patients/${patientId}/payer_programs`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      });
      const result = await response.json();

      if (response.ok && result.success !== false) {
        const data = Array.isArray(result.data) ? result.data[0] ?? null : result.data ?? null;
        setPayerData(data);
      }
    } catch {
      // Silently swallow — section simply won't render
    } finally {
      setLoading(false);
    }
  };

  if (loading || !payerData || !payerData.programs || payerData.programs.length === 0) return null;

  return (
    <Grid container className="panel-container" borderRadius={"4px"}>
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container direction="row" className="admin-header">
            <Grid item xs={12} className="box-header">
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                paddingX={3}
                alignItems={"center"}
                display={"flex"}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ marginY: "16px" }}>
                  <h3>Payer Programs</h3>
                  {(() => {
                    const match = payerData.payer_name.match(/\(([^)]+)\)/);
                    const label = `Payer: ${match ? match[1] : payerData.payer_name}`;
                    return <CustomChip label={label} type="success" size="small" />;
                  })()}
                </Stack>
                <Link
                  className="action-link add-encounter"
                  onClick={() => {
                    setReFetchingKey(Math.random());
                    setShowHistoryModal(true);
                  }}
                  sx={{ mr: 3 }}
                >
                  <img
                    src={images.history_icon}
                    alt="History Icon"
                    style={{
                      width: "28px",
                      marginBottom: "-8px",
                      marginRight: "2px",
                    }}
                  />
                  <span className="app-user-text">
                    Show Payer Program Assignment History
                  </span>
                </Link>
              </Stack>
            </Grid>
          </Grid>

          <Grid
            container
            className="tabs-container"
            sx={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            <Grid
              item
              xs={12}
              pb={2}
              sx={{ backgroundColor: "#EFE9E8" }}
            >
              <Grid
                item
                xs={12}
                sx={{ bgcolor: "#EFE9E8" }}
              >
                {payerData.programs.map((program) => (
                  <PayerProgramCard key={program.payer_program_id} program={program} patientId={patientId} />
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <AssignmentHistoryModal
        type={"payer_program"}
        open={showHistoryModal}
        onCloseModal={() => setShowHistoryModal(false)}
        histories={assignmentHistories}
        loading={historiesLoading}
      />
    </Grid>
  );
};

export default PayerPrograms;
